import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Camera, Images, Package, CreditCard, Mail, ArrowRight, Check, Heart, User, Smartphone } from 'lucide-react';

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // If already logged in, redirect to admin
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin');
    }
  }, [isAuthenticated, navigate]);

  const features = [
    {
      icon: Images,
      title: 'Gallery Management',
      description: 'Manage hundreds of galleries with ease. Bulk import clients and send links in one click.'
    },
    {
      icon: Package,
      title: 'Custom Packages',
      description: 'Pre-defined packages for sports, school, and portraits with customization options.'
    },
    {
      icon: CreditCard,
      title: 'Secure Payments',
      description: 'Accept payments in CAD via Stripe. Track orders and revenue in your dashboard.'
    },
    {
      icon: Mail,
      title: 'Bulk Email',
      description: 'Send gallery links to all your clients at once. Save hours on every event.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_photo-gallery-orders/artifacts/59pp4syw_BEP%20Logo%202017-1.png" 
                alt="Bright Eyes Photography"
                className="h-12 w-auto"
              />
            </div>
            <div className="flex items-center gap-3">
              <Link to="/app">
                <Button variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="mobile-app-btn">
                  <Smartphone className="h-4 w-4 mr-2" />
                  Mobile App
                </Button>
              </Link>
              <Link to="/client/auth">
                <Button variant="outline" className="border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="client-portal-btn">
                  <User className="h-4 w-4 mr-2" />
                  Client Portal
                </Button>
              </Link>
              <Link to="/auth">
                <Button className="btn-primary" data-testid="header-login-btn">
                  Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/50 to-stone-50" />
        <div className="relative max-w-6xl mx-auto px-6 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div>
                <p className="text-amber-700 font-medium tracking-widest uppercase text-sm mb-4">
                  Gallery & Order Management
                </p>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-stone-800 leading-tight">
                  Your Photography<br />
                  <span className="text-amber-700">Business, Simplified</span>
                </h1>
              </div>
              <div className="elegant-divider !mx-0" />
              <p className="text-lg text-stone-600 max-w-lg leading-relaxed">
                Manage your sports and school photography galleries, process orders with 
                custom backgrounds, and send gallery links to hundreds of clients in minutes.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/auth">
                  <Button size="lg" className="btn-action text-base px-8" data-testid="hero-get-started-btn">
                    Get Started <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
              <div className="flex items-center gap-6 pt-2">
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <Check className="h-5 w-5 text-amber-600" />
                  <span>350+ galleries</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <Check className="h-5 w-5 text-amber-600" />
                  <span>CAD payments</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-stone-600">
                  <Check className="h-5 w-5 text-amber-600" />
                  <span>Bulk emails</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-[4/5] rounded-sm overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                  alt="Portrait photography"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-sm shadow-xl p-5 border border-stone-100">
                <div className="flex items-center gap-3">
                  <div className="bg-amber-100 rounded-full p-2">
                    <Heart className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-stone-800">Memories Delivered</p>
                    <p className="text-xs text-stone-500">Stony Plain, Alberta</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-stone-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-amber-700 font-medium tracking-widest uppercase text-sm mb-4">
              Features
            </p>
            <h2 className="text-3xl sm:text-4xl font-heading text-stone-800">
              Everything You Need
            </h2>
            <div className="elegant-divider mt-6" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-sm p-8 border border-stone-200 card-hover text-center"
              >
                <div className="bg-amber-100 rounded-full w-14 h-14 flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="h-7 w-7 text-amber-700" />
                </div>
                <h3 className="font-heading text-xl text-stone-800 mb-3">
                  {feature.title}
                </h3>
                <p className="text-stone-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-stone-800">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-heading text-white mb-6">
            Ready to Streamline Your Business?
          </h2>
          <p className="text-lg text-stone-300 mb-8 max-w-2xl mx-auto">
            Stop struggling with tools that weren't built for high volume. 
            Start managing your galleries efficiently today.
          </p>
          <Link to="/auth">
            <Button size="lg" className="btn-action text-base px-8" data-testid="cta-get-started-btn">
              Get Started <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-stone-200 py-8">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_photo-gallery-orders/artifacts/59pp4syw_BEP%20Logo%202017-1.png" 
              alt="Bright Eyes Photography"
              className="h-10 w-auto"
            />
            <p className="text-sm text-stone-500">
              Stony Plain, Alberta • © 2024 All rights reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
